import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  booleanAttribute,
  signal,
} from '@angular/core';
import { Icon, IconsComponent } from '../icons/icons.component';
import { PopConfirmComponent } from './pop-confirm/pop-confirm.component';

export type ButtonType = 'button' | 'submit';
@Component({
  selector: 'vti-button',
  templateUrl: './button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, IconsComponent, PopConfirmComponent],
})
export class ButtonComponent implements OnInit, OnChanges {
  @Input({ required: true }) public text!: string;
  @Input() public type: ButtonType = 'button';
  @Input() public icon: Icon | undefined;
  @Input({ transform: booleanAttribute }) public ghost: boolean | undefined = false;
  @Input({ transform: booleanAttribute }) public inverseColorPattern: boolean | undefined = false;
  @Input({ transform: booleanAttribute }) public loading: boolean | undefined = false;
  @Input({ transform: booleanAttribute }) public disabled: boolean | undefined = false;
  @Input() public popconfirm: string | undefined;
  @Input() public popconfirmTitle = '';
  @Input() public ghostBackgroundColor = '';

  @Output() public onClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() public onConfirm: EventEmitter<void> = new EventEmitter<void>();

  public ngOnInit(): void {
    if (this.inverseColorPattern) {
      this.disableClass = 'rounded-md bg-vti-600 px-3 py-2 text-white shadow-sm';
      this.enableClass =
        'rounded-md bg-white px-3 py-2 text-vti-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vti-600';
    } else if (!this.ghost) {
      this.disableClass = 'rounded-md bg-vti-600 px-3 py-2 text-white shadow-sm';
      this.enableClass =
        'rounded-md bg-vti-900 px-3 py-2 text-white shadow-sm hover:bg-vti-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vti-600';
    } else {
      this.disableClass = 'leading-6 bg-white text-vti-500';
      this.enableClass = 'rounded-md px-3 py-2 leading-6 bg-white text-vti-900 hover:bg-vti-100';

      if (this.ghostBackgroundColor) {
        this.disableClass = this.disableClass.replace('bg-white', this.ghostBackgroundColor);
        this.enableClass = this.enableClass.replace('bg-white', this.ghostBackgroundColor);
      }
    }

    if (this.disabled) {
      this.classes.update(() => this.disableClass);
    } else {
      this.classes.update(() => this.enableClass);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['loading']?.currentValue) {
      this.classes.update((classes) => {
        return classes.replace(' bg-vti-900 hover:bg-vti-900 ', ' bg-vti-500 hover:bg-vti-500 ');
      });
    } else if (changes['loading']?.currentValue === false) {
      this.classes.update((classes) => {
        return classes.replace(' bg-vti-500 hover:bg-vti-500 ', ' bg-vti-900 hover:bg-vti-900 ');
      });
    }

    if (changes['disabled']?.currentValue) {
      this.classes.update(() => this.disableClass);
    } else if (changes['disabled']?.currentValue === false) {
      this.classes.update(() => this.enableClass);
    }
  }

  public buttonClick(): void {
    if (this.popconfirm !== undefined) {
      this.popConfirmVisible.update(() => true);
    } else {
      this.onClick.emit();
    }
  }

  public popConfirmOkClick(): void {
    this.onConfirm.emit();
    this.popConfirmVisible.update(() => false);
  }

  public popConfirmCancelClick(): void {
    this.popConfirmVisible.update(() => false);
  }

  private disableClass = '';
  private enableClass = '';

  public classes = signal<string>('');
  public popConfirmVisible = signal<boolean>(false);
}
